import { useCallback, useEffect, useState, type FormEvent } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { NetworkInvitationsBanner } from "../components/NetworkInvitationsBanner";

interface NetworkConnection {
  uuid: string;
  status: "Pending" | "Active" | "Revoked";
  direction: "buyer" | "supplier";
  counterpart: { uuid: string; name: string; slug: string };
  invitedAt: string;
  acceptedAt: string | null;
  revokedAt: string | null;
  revokeReason: string | null;
}

const formatDateUk = (iso: string | null): string => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(2, "0")}/${d.getFullYear()}`;
};

const STATUS_TONE: Record<NetworkConnection["status"], string> = {
  Pending: "bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-200",
  Active: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-200",
  Revoked: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

export function Network() {
  const { slug } = useParams<{ slug: string }>();
  const { activeOrganisation } = useAuth();
  const isAdmin = activeOrganisation?.role === "Admin";

  const [connections, setConnections] = useState<NetworkConnection[] | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  const [supplierSlug, setSupplierSlug] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [busyUuid, setBusyUuid] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await api<{ connections: NetworkConnection[] }>("/network/connections");
      setConnections(res.connections);
    } catch (err) {
      setErrors(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const buyers = (connections ?? []).filter((c) => c.direction === "buyer");
  const suppliers = (connections ?? []).filter((c) => c.direction === "supplier");

  const onInvite = async (event: FormEvent) => {
    event.preventDefault();
    setSubmitting(true);
    setErrors([]);
    try {
      await api("/network/connections/invite", {
        method: "POST",
        body: { supplierSlug: supplierSlug.trim().toLowerCase() },
      });
      setSupplierSlug("");
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onAccept = async (uuid: string) => {
    setBusyUuid(uuid);
    setErrors([]);
    try {
      await api(`/network/connections/${uuid}/accept`, { method: "POST" });
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusyUuid(null);
    }
  };

  const onDecline = async (uuid: string) => {
    setBusyUuid(uuid);
    setErrors([]);
    try {
      await api(`/network/connections/${uuid}/decline`, { method: "POST", body: {} });
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusyUuid(null);
    }
  };

  const onRevoke = async (uuid: string) => {
    setBusyUuid(uuid);
    setErrors([]);
    try {
      await api(`/network/connections/${uuid}/revoke`, { method: "POST", body: {} });
      await refresh();
    } catch (err) {
      setErrors(formatApiError(err));
    } finally {
      setBusyUuid(null);
    }
  };

  return (
    <div className="space-y-6">
      <NetworkInvitationsBanner />

      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          Supplier Trust Network
        </h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Connect to your suppliers by their organisation slug. Once they accept, any evidence they
          publish to the network is visible to you and you can pin it to your own vault. Either
          party can revoke the connection at any time.
        </p>
      </div>

      {errors.length > 0 ? (
        <div
          id="network-errors"
          role="alert"
          className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-200"
        >
          {errors[0]}
        </div>
      ) : null}

      {isAdmin ? (
        <section className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            Invite a supplier
          </h2>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Enter the supplier&rsquo;s ISO Clarity URL slug. They must be signed up already — share
            the signup link if not.
          </p>
          <form onSubmit={onInvite} className="mt-3 flex flex-wrap items-center gap-2">
            <span className="text-sm text-slate-500 dark:text-slate-400">isoclarity.co.uk/o/</span>
            <input
              type="text"
              value={supplierSlug}
              onChange={(e) => setSupplierSlug(e.target.value)}
              placeholder="acme-widgets"
              pattern="^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$"
              required
              aria-label="Supplier slug"
              aria-invalid={errors.length > 0 || undefined}
              aria-describedby={errors.length > 0 ? "network-errors" : undefined}
              className="block rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm shadow-sm focus:border-brand-500 focus:ring-brand-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
            <button
              type="submit"
              disabled={submitting || supplierSlug.trim().length < 3}
              className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "Sending…" : "Send invitation"}
            </button>
          </form>
        </section>
      ) : null}

      <ConnectionList
        title="Your suppliers"
        emptyHint="When suppliers accept your invitations they appear here."
        connections={buyers}
        slug={slug}
        isAdmin={isAdmin}
        busyUuid={busyUuid}
        onRevoke={onRevoke}
        showSupplierEvidenceLink
      />

      <ConnectionList
        title="Buyers connected to you"
        emptyHint="When you accept a buyer's invitation they appear here."
        connections={suppliers}
        slug={slug}
        isAdmin={isAdmin}
        busyUuid={busyUuid}
        onAccept={onAccept}
        onDecline={onDecline}
        onRevoke={onRevoke}
      />
    </div>
  );

  function ConnectionList({
    title,
    emptyHint,
    connections,
    slug,
    isAdmin,
    busyUuid,
    onAccept,
    onDecline,
    onRevoke,
    showSupplierEvidenceLink,
  }: {
    title: string;
    emptyHint: string;
    connections: NetworkConnection[];
    slug: string | undefined;
    isAdmin: boolean;
    busyUuid: string | null;
    onAccept?: (uuid: string) => Promise<void>;
    onDecline?: (uuid: string) => Promise<void>;
    onRevoke: (uuid: string) => Promise<void>;
    showSupplierEvidenceLink?: boolean;
  }) {
    return (
      <section>
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
            {title}
          </h2>
          {showSupplierEvidenceLink && slug ? (
            <Link
              to={`/o/${slug}/network/supplier-evidence`}
              className="text-xs font-medium text-brand-600 hover:text-brand-700 dark:text-brand-300"
            >
              View supplier evidence →
            </Link>
          ) : null}
        </div>
        {connections.length === 0 ? (
          <p className="mt-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 p-4 text-sm text-slate-600 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400">
            {emptyHint}
          </p>
        ) : (
          <ul className="mt-2 space-y-2">
            {connections.map((c) => (
              <li
                key={c.uuid}
                className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white p-3 dark:border-slate-700 dark:bg-slate-800"
              >
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-900 dark:text-slate-100">
                    {c.counterpart.name}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    slug: <span className="font-mono">{c.counterpart.slug}</span> · invited{" "}
                    {formatDateUk(c.invitedAt)}
                    {c.acceptedAt ? ` · accepted ${formatDateUk(c.acceptedAt)}` : ""}
                    {c.revokedAt ? ` · revoked ${formatDateUk(c.revokedAt)}` : ""}
                  </p>
                </div>
                <span
                  className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${STATUS_TONE[c.status]}`}
                >
                  {c.status}
                </span>
                {isAdmin ? (
                  <div className="flex gap-2">
                    {c.status === "Pending" &&
                    c.direction === "supplier" &&
                    onAccept &&
                    onDecline ? (
                      <>
                        <button
                          type="button"
                          onClick={() => void onAccept(c.uuid)}
                          disabled={busyUuid === c.uuid}
                          className="rounded-md bg-brand-600 px-3 py-1 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                        >
                          Accept
                        </button>
                        <button
                          type="button"
                          onClick={() => void onDecline(c.uuid)}
                          disabled={busyUuid === c.uuid}
                          className="rounded-md border border-slate-300 bg-white px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
                        >
                          Decline
                        </button>
                      </>
                    ) : null}
                    {c.status === "Active" ? (
                      <button
                        type="button"
                        onClick={() => void onRevoke(c.uuid)}
                        disabled={busyUuid === c.uuid}
                        className="rounded-md border border-red-200 bg-white px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-60 dark:border-red-800 dark:bg-slate-800 dark:text-red-200 dark:hover:bg-red-950/40"
                      >
                        Revoke
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </section>
    );
  }
}
